import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getSubscriberById, updateSubscriber, approveSubscriber, rejectSubscriber, UpdateSubscriberData } from '../../../services/subscriberService';
import { Subscriber } from '../../../types/subscriber';
import {  SubscriberStatus } from '../../../types/enums';
import formStyles from '../trainees/TraineeForm.module.css';

type FormInputs = Required<UpdateSubscriberData>;

const EditSubscriberPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const subscriberId = Number(id);

  const [subscriber, setSubscriber] = useState<Subscriber | null>(null);

  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm<FormInputs>();

  useEffect(() => {
    if (!subscriberId) return;
    const fetchSubscriberData = async () => {
      try {
        const data = await getSubscriberById(subscriberId);
        setSubscriber(data);
        reset(data);
      } catch (error) {
        toast.error('فشل في جلب بيانات المشترك.');
        navigate('/admin/subscribers/list');
      }
    };
    fetchSubscriberData();
  }, [subscriberId, navigate, reset]);

  const onUpdateSubmit: SubmitHandler<FormInputs> = (data) => {
    const promise = updateSubscriber(subscriberId, data);
    toast.promise(promise, {
      loading: 'جاري تحديث البيانات...',
      success: () => {
        navigate('/admin/subscribers/list');
        return 'تم تحديث البيانات بنجاح!';
      },
      error: (err) => err.response?.data?.message || 'فشل في تحديث البيانات.',
    });
  };

  const handleApproval = (action: 'approve' | 'reject') => {
    const promise = action === 'approve' ? approveSubscriber(subscriberId) : rejectSubscriber(subscriberId);
    const messages = {
      approve: { loading: 'جاري الموافقة على المشترك...', success: 'تمت الموافقة على المشترك بنجاح!' },
      reject: { loading: 'جاري رفض المشترك...', success: 'تم رفض المشترك بنجاح!' },
    };

    toast.promise(promise, {
      loading: messages[action].loading,
      success: (updatedSubscriber) => {
        setSubscriber(updatedSubscriber);
        reset(updatedSubscriber);
        return messages[action].success;
      },
      error: (err) => err.response?.data?.message || 'فشلت العملية.',
    });
  };

  if (!subscriber) return <div>جاري التحميل...</div>;

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>تفاصيل المشترك: {subscriber.name}</h2>
        
        <div className={formStyles.detailsSection}>
          <p><strong>البريد الإلكتروني:</strong> {subscriber.user?.email}</p>
          <p><strong>حالة الحساب الحالية:</strong> {subscriber.status}</p>
        </div>

        <form onSubmit={handleSubmit(onUpdateSubmit)} className={formStyles.form}>
          <div className={formStyles.formField}>
            <label htmlFor="name">الاسم</label>
            <input id="name" {...register('name', { required: true })} className={formStyles.input} />
          </div>
          <div className={formStyles.formField}>
            <label htmlFor="phone">الهاتف</label>
            <input id="phone" {...register('phone', { required: true })} className={formStyles.input} />
          </div>
          <div className={formStyles.formField}>
            <label htmlFor="qualification">المؤهل</label>
            <input id="qualification" {...register('qualification', { required: true })} className={formStyles.input} />
          </div>
          <div className={formStyles.formField}>
            <label htmlFor="graduation_year">سنة التخرج</label>
            <input id="graduation_year" type="number" {...register('graduation_year', { required: true, valueAsNumber: true })} className={formStyles.input} />
          </div>

          <div className={`${formStyles.buttonContainer} ${formStyles.fullWidth}`}>
            <Link to="/admin/subscribers/list" className={formStyles.cancelButton}>العودة للقائمة</Link>
            <button type="submit" disabled={isSubmitting} className={formStyles.submitButton}>
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>

        {/* أزرار الموافقة والرفض */}
        {subscriber.status === SubscriberStatus.PENDING && (
          <div className={`${formStyles.buttonContainer} ${formStyles.fullWidth}`} style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
            <button onClick={() => handleApproval('reject')} className={formStyles.rejectButton}>رفض المشترك</button>
            <button onClick={() => handleApproval('approve')} className={formStyles.approveButton}>الموافقة على المشترك</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditSubscriberPage;
